import React, { Component, useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames/bind';
import Input from '../Input/Input';
import createFakeEvent from '../../lib/createFakeEvent';

/**
 * `UnitInput` is an input field that allows input of a value and a unit, for instance allowing the user to set a period of
 * either days, weeks, months or years. The component also supports converting values when switching units.
 *
 * The unit will be displayed after the value and cannot be edited directly, but the user can select another unit from the
 * dropdown that is displayed when the input field has the focus. The component will automatically display different labels
 * depending on the value: "1 year" vs. "2 years".
 *
 * Units are specified as an array of objects defining the singular and plural labels, e.g.
 * ````
 * {
 *   unit: 'day',
 *   many: 'days',
 *   factor: x, // optional unit value conversion factor
 * }
 * ````
 * The "unit" label will be used when the value is 1 while the "many" label will be used when the value is different from 1.
 *
 * If *convert* is `true`, the component will use the factors in the units to convert the value when switching units. A
 * unit's factor defines the unit's value based on the default unit's factor - that is; a gram's factor is 0.001 if the
 * default unit is 1 kg.
 *
 * When using conversion the *value* must be set as a factor of the default unit's factor as the value is always reported in
 * the default unit's factor. This basically means that when using conversion the unit is just a display setting. E.g. if the
 * default unit is kg and you pass the value 1 the input will display "1 kg". If you pass the value 1 and set the unit to
 * "grams", the input will display "1000 grams". See the weight example above on how to use conversion.
 *
 * `UnitInput` has a built in unit `UnitInput.PERIOD_DAYS_WEEKS_MONTHS_YEARS` that can be used to select between days, weeks,
 * months and years. Value must be number of seconds in a period when using this unit set. When using conversion with these
 * units be sure to *rounded* to `true` otherwise you will end up with rounding errors as 365 days is not exactly 12 30-day
 * months. Calculations will be based on a 365 days in a year and 30 days in a month. When using these units with conversion
 * the value must be set to the number of seconds in the period. See the period conversion example above.
 *
 * This component accepts all props valid for {@link Input} except for *options* which will be set to your units.
 *
 * @property {string} className The class name of this component. Will be appended to "core-unit-input".
 * @property {boolean} clearable If set to `true`, a **Clear** button will be shown on hover and focus. Defaults to `true`.
 * @property {boolean} convert If set to `true`, selecting another unit will result in a conversion of the value. When using
 * this prop each unit must have a factor so that the proper conversion can be done.
 * @property {boolean} defaultUnitIndex The index of your default unit. This unit will be used when *value* does not specify
 * the unit or *unit* is not set. Defaults to index 0 - the first unit in your *units* array.
 * @property {boolean} disabled The component will be disabled if this prop is set to `true`. Defaults to `false`.
 * @property {node} errorMessage An error {@link Notice} that will be shown below the component. Defaults to no Notice.
 * @property {string} iconName Custom icon that will be displayed to the right in the input field. The icon name can be any
 * of the embedded icons in {@link Icon}.
 * @property {string} id The ID of this component. Defaults to variant of the label provided.
 * @property {node} infoMessage An info Notice that will be shown below the component. Defaults to no Notice.
 * @property {string} label The label of the input field. Defaults to no label.
 * @property {function} onBlur A callback that will be called when the component has lost focus.
 * @property {function} onChange The change listener that will be called whenever the user changes the input value.
 * `event.target.value` will hold the selected value **including** the unit **if** you passed a value string containing the
 * unit, or just the value if you specified unit using *unit*. `event.target.unit` will hold the current selected *unit*.
 * `event.target.valueString` will hold a string representation of the value and the unit.
 * @property {function} onClearNotice Callback that is called when the user dismiss the optional Notice.
 * @property {function} onFocus A callback that is called when the component gets the input focus.
 * @property {string} placeholder The placeholder text for this input field. Defaults to no placeholder.
 * @property {boolean} readOnly Makes this input field read only. A read only input field will have a transparent background.
 * You may use *placeholder* as a default value that will be displayed when the actual value is undefined.
 * @property {boolean} required If set to `true`, a required indicator will be displayed inside the input field. Defaults to
 * `false`.
 * @property {boolean} rounded When set to `true`, converted values are rounded off. This is handy when e.g. converting
 * periods as the number of seconds in a year varies. Setting *rounded* to `true` will ensure that 1 year is converted to
 * exactly 365 days, 52 weeks or 12 months. Defaults to `false`.
 * @property {boolean} showOptionsOnFocus By default auto completion is only shown when the user has typed something. Setting
 * this prop to `true` will result in the autocompletion dropdown to be displayed when the input field gets the focus.
 * Defaults to `true`.
 * @property {boolean} showUnderline The component will display a line under the input field to indicate input focus. You
 * can set the value of this prop to `false` to turn this off. Defaults to `true`.
 * @property {node} successMessage A success Notice that will be shown below the component. Defaults to no Notice.
 * @property {boolean} sortOptions If `true`, the options will be sorted alphabetically by label or value. Defaults to
 * `false`.
 * @property {string | object} unit The unit that should be displayed. Can be either a string with the unit label (either
 * singular or plural label), or the unit object itself. If no unit is specified, the unit will either be extracted from
 * *value* if it is a string, or default to the unit donated by *defaultUnitIndex*.
 * @property {array} units Array of unit objects containing singular and plural labels for a unit, e.g.
 * `{ unit: 'day', many: 'days', factor: x }`. `UnitInput` has a unit array named `UnitInput.PERIOD_DAYS_WEEKS_MONTHS_YEARS`
 * built into it that can be used to select between days, weeks, months and years.
 * @property {boolean} useIsoDuration By default this component use seconds as input. This flag enables the use of
 * ISO-8601 duration as input.
 * NB: This will disable some other flags (*convert*, *rounded*). It will also not use *factor*
 * from `UnitInput.PERIOD_DAYS_WEEKS_MONTHS_YEARS`, only `unit` and `many`. There is only support for one unit e.g `P3M` for three months.
 * Lastly the *onChange* function will recieve the new ISO duration instead of the *event*.
 * @property {number | string} value The value of your input period. It can either be a string containing the value and the
 * unit e.g. "10 months", or you may pass just the number value and use the *unit* prop to set the unit. If *convert* is
 * `true`, *value* must be set in a factor of the default unit's factor. Defaults to no value.
 * @property {node} warningMessage A warning Notice that will be shown below the component. Defaults to no Notice.
 *
 * @see DateInput, Icon, Input, Notice, TimeInput
 */
const UnitInput = (props) => {
  const { convert, defaultUnitIndex, onChange, rounded, units, className, useIsoDuration, ...rest } = props;

  const refInput = useRef();
  const [currentValue, setCurrentValue] = useState('');
  const [currentUnit, setCurrentUnit] = useState({});

  useEffect(() => {
    if (currentValue !== props.value || currentUnit !== props.unit) {
      getChangeState(props);
    }
  }, [props]);

  const getChangeState = ({ unit, value }) => {
    useIsoDuration ? calculateIsoDuration(value) : calculate(value, unit);
  };

  /* eslint-disable no-param-reassign */
  const calculate = (value, unit) => {
    const parsed = typeof value === 'string' ? value.split(' ') : [value];
    value = parseFloat(parsed[0]) || '';
    if (value) {
      if (unit) {
        unit = findUnit(unit);
        value /= unit.factor || 1;
      } else {
        for (let i = units.length - 1; i >= 0; i--) {
          if (units[i].factor && value % units[i].factor === 0) {
            unit = units[i];
            value /= units[i].factor;
            break;
          }
        }
      }
    }
    unit = findUnit(unit || (parsed.length > 1 && parsed[1])) || units[defaultUnitIndex];
    value = rounded ? Math.floor(value) : value;
    const label = value === 1 ? 'unit' : 'many';
    setCurrentValue(value);
    setCurrentUnit(unit);
  };

  /* eslint-enable no-param-reassign */
  const calculateIsoDuration = (value) => {
    let unit;
    const isoUnit = value[value.length - 1];

    if (isoUnit ==='Y') unit = 'year';
    else if (isoUnit ==='M') unit = 'month';
    else if (isoUnit ==='W') unit = 'week';
    else unit = 'day';

    value = parseFloat(value.substr(1, value.length - 2)) || 0;
    const label = value === 1 ? 'unit' : 'many';

    setCurrentValue(value);
    setCurrentUnit(findUnit(unit));
  };

  const findUnit = (unit) => (typeof unit === 'string' ? props.units.find(u => unit === u.unit || unit === u.many) || props.units[props.defaultUnitIndex] : unit);

  const getValue = (value) => (typeof value === 'string' ? parseFloat(value.split(' ')[0]) : value);

  const handleOnChange = (e, autoComplete) => {
    if (onChange) {
      let value;
      let displayValue;
      let unit;

      if (autoComplete) {
        const valueContainsUnit = typeof props.value === 'string' && props.value.indexOf(' ') > -1;

        unit = findUnit(e.target.value);
        value = getValue(props.value) || 1;
        if (!convert && !valueContainsUnit) {
          value = value / (currentUnit.factor || 1);
          value = value * (unit.factor || 1);
        }
        displayValue = convert ? value / unit.factor : value;
      } else {
        unit = currentUnit;
        value = e.target.value * (unit.factor || 1);
        displayValue = e.target.value;
      }
      if (!unit) {
        unit = units[defaultUnitIndex];
      }
      const textValue = `${displayValue} ${displayValue === 1 || displayValue === '1' ? unit.unit : unit.many || unit.unit}`;
      const event = createFakeEvent(typeof props.value === 'string' && props.value.indexOf(' ') >= 0 ? textValue : value, e);
      event.target.unit = unit;
      event.target.valueString = textValue;
      onChange(event);
    }
  };

  const handleOnChangeIsoDuration = (e) => {
    const changedValue = e.target.value;
    const isUnit = isNaN(changedValue);
    const isoDuration = isUnit ?
      `P${currentValue}${changedValue.substr(0,1).toUpperCase()}` :
      `P${changedValue}${currentUnit.unit.substr(0,1).toUpperCase()}`;
    onChange(isoDuration);
  };

  const handleOnClick = (e) => {
    refInput.current.focus();
    refInput.current.moveCaretToEnd();
  };

  const getChars = () => {
    return `${currentValue}`.length;
  };

  const getOptions = () => {
    const label = currentValue === 1 ? 'unit' : 'many';
    return units.map(u => u[label] || u.unit);
  };

  const chars = getChars();
  const options = getOptions();
  let unit;
  let style;
  if (currentValue !== '' && currentValue >= 0 && currentUnit) {
    unit = currentValue === '1'  || currentValue === 1 ? currentUnit.unit : (currentUnit.many || currentUnit.unit);
    style = { width: `${chars + (props.readOnly ? 1 : 2)}ch`};
  }

  return (
    <Input {...rest} className={cx('core-unit-input', className)} ref={refInput}
      options={options} value={currentValue} onChange={useIsoDuration ? handleOnChangeIsoDuration : handleOnChange} style={style}>
      <span className="unit" onClick={handleOnClick}>{unit}</span>
    </Input>
  );
};

UnitInput.PERIOD_DAYS_WEEKS_MONTHS_YEARS = [{
  factor: 86400,
  unit: 'day',
  many: 'days',
}, {
  factor: 86400 * 7,
  unit: 'week',
  many: 'weeks',
}, {
  factor: 86400 * 30,
  unit: 'month',
  many: 'months',
}, {
  factor: 86400 * 365,
  unit: 'year',
  many: 'years',
}];

UnitInput.propTypes = {
  className: PropTypes.string,
  clearable: PropTypes.bool,
  convert: PropTypes.bool,
  defaultUnitIndex: PropTypes.number,
  disabled: PropTypes.bool,
  errorMessage: PropTypes.node,
  iconName: PropTypes.string,
  id: PropTypes.string,
  infoMessage: PropTypes.node,
  label: PropTypes.string,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onClearNotice: PropTypes.func,
  onFocus: PropTypes.func,
  placeholder: PropTypes.string,
  readOnly: PropTypes.bool,
  required: PropTypes.bool,
  rounded: PropTypes.bool,
  showOptionsOnFocus: PropTypes.bool,
  showUnderline: PropTypes.bool,
  successMessage: PropTypes.node,
  unit: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
  ]),
  units: PropTypes.arrayOf(PropTypes.object).isRequired,
  useIsoDuration: PropTypes.bool,
  warningMessage: PropTypes.node,
};

UnitInput.defaultProps = {
  clearable: true,
  defaultUnitIndex: 0,
  filterOptions: false,
  showOptionsOnFocus: true,
  showUnderline: true,
  sortOptions: false,
  useIsoDuration: false,
  value: '',
};

UnitInput.displayName = 'UnitInput';

export default UnitInput;
